/**
 * Callback Registry for React Command Flow
 *
 * Core registry functionality for managing and executing custom callbacks.
 */

/**
 * Creates a callback registry with trigger functions
 * @param {Object} callbacks - Object containing named callback functions
 * @returns {Object} - Callback registry with trigger methods
 */
export function createCallbackRegistry(callbacks = {}) {
  const registry = { ...callbacks };

  return {
    // Register a new callback
    register: (name, callback) => {
      if (typeof callback !== "function") {
        console.warn(`Callback "${name}" must be a function`);
        return;
      }
      registry[name] = callback;
    },

    // Unregister a callback
    unregister: (name) => {
      delete registry[name];
    },

    // Get all registered callbacks
    getAll: () => ({ ...registry }),

    // Get a specific callback
    get: (name) => registry[name],

    // Check if callback exists
    has: (name) => typeof registry[name] === "function",

    // Trigger a callback with data
    trigger: async (name, data = {}, context = {}) => {
      const callback = registry[name];

      if (!callback) {
        console.warn(`Callback "${name}" not found in registry`);
        return null;
      }

      if (typeof callback !== "function") {
        console.warn(`Callback "${name}" is not a function`);
        return null;
      }

      try {
        console.log(`🔥 Triggering custom callback: ${name}`, {
          data,
          context,
        });
        const result = await callback(data, context);
        console.log(`✅ Callback "${name}" completed:`, result);
        return result;
      } catch (error) {
        console.error(`❌ Error in callback "${name}":`, error);
        throw error;
      }
    },

    // Trigger multiple callbacks in sequence
    triggerSequence: async (callbackNames, data = {}, context = {}) => {
      const results = [];

      for (const name of callbackNames) {
        try {
          const result = await registry.trigger(name, data, context);
          results.push({ name, result, success: true });
        } catch (error) {
          results.push({ name, error, success: false });
        }
      }

      return results;
    },

    // Trigger multiple callbacks in parallel
    triggerParallel: async (callbackNames, data = {}, context = {}) => {
      const promises = callbackNames.map(async (name) => {
        try {
          const result = await registry.trigger(name, data, context);
          return { name, result, success: true };
        } catch (error) {
          return { name, error, success: false };
        }
      });

      return await Promise.all(promises);
    },
  };
}
